import { Component, OnInit, OnDestroy } from '@angular/core';
import { PaymentRequestDto } from '../../../model/payment-request/payment-request.dto';
import {
  IVippsError,
  getErrorMessage,
  VippsResponse,
  VippsStatus,
  VippsRequestParams,
} from 'src/app/service/vipps/vipps-response.dto';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryParamsService } from 'src/app/service/query-params/query-params.service';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  CountryCodes,
  formatPhoneNumber,
  phoneNumberValidator,
} from 'web-component-library/projects/component-library/src/public-api';
import { RouteComponent } from '../../route.component';
import { HeaderService } from '@app/service/header/header.service';
import {
  CheckoutState,
  ResumeTokenType,
  StateControlParameters,
  StateService,
} from '@app/service/state/state.service';
import { startWith, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '@app/component/dialog/error/error-dialog.component';
import { VippsService } from '@app/service/vipps/vipps.service';
import { Subscription, timer } from 'rxjs';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { DeviceDetectorService, OS } from 'ngx-device-detector';
import { NoRoute } from '@assets/val/route-constants';

@Component({
  selector: 'no-vipps',
  templateUrl: './no-vipps.component.html',
  styleUrls: ['../../page-shared.css', './no-vipps.component.scss'],
})
export class VippsComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  resumePolling(vippsId: string) {
    if (!!vippsId) {
      try {
        this.startPolling();
      } catch (err) {
        this.handleError(err);
        console.warn('resuming polling got error', err);
      }
    }
  }

  phoneNumber: string;
  isPhone: boolean;
  error: IVippsError[] = null;
  modalActive: boolean = false;
  resumeToken: string;
  form: FormGroup;
  paymentRequest: PaymentRequestDto;
  loading: boolean = true;
  paymentInProgress: boolean;
  showError: boolean = false;
  formLayout: boolean = false;
  manualDeeplinkEnabled: boolean = false;
  mobileStatusMessage: string = 'Forbereder betaling...';
  paymentStatusOtherDevice: string;
  activeDepositId: string;
  vippsLandingRedirectUrl: string = null;
  firstTimeCaptured: boolean = true;
  returnedFromVipps: boolean = false;
  vippsPaymentStatus: VippsStatus = VippsStatus.VOID;
  pollingSubscription: Subscription;
  shouldOverrideIsPhone: boolean = false;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private deviceDetectorService: DeviceDetectorService,
    private queryParamsService: QueryParamsService,
    private dialogService: MatDialog,
    private formBuilder: FormBuilder,
    private loggingService: LoggingService,
    private vippsService: VippsService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit() {
    this.paymentRequest = await this.vippsService.getPaymentRequest();
    this.phoneNumber = this.paymentRequest.phoneNumber || '';
    this.setupForm();

    if (this.deviceDetectorService.isMobile()) {
      this.isPhone = true;
    }

    // Vipps payment is navigated here in one of two ways:
    // a) First time visiting the vipps page in the checkout flow
    //    Create a "resumable state token" so we can resume checkout-flow state when redirected to vipps (from vipps app and fallback URL)
    if (!this.queryParamsService.getQueryParams().vippsId) {
      await this.fetchState();
      this.resumeToken = await this.createResumeToken();
      if (this.resumeToken === null) {
        this.loggingService.warn(
          'vipps frontend: vippsId found as URL param, but no resume checkoutState token could be found'
        );
      }
    }

    // b) Already been in the checkout-flow and is redirected back from vipps
    if (this.queryParamsService.getQueryParams().vippsId) {
      if (!!this.queryParamsService.getClientSideParams()) {
        // Recover the state from "resumable state token"
        const clientSideParams = this.queryParamsService.getClientSideParams();
        // Extract only the UUID part (before any & parameters)
        this.resumeToken = clientSideParams.split('&')[0];

        await this.resumeState(this.resumeToken);
      }

      this.activeDepositId = this.queryParamsService.getQueryParams().vippsId;
      this.resumePolling(this.activeDepositId);
      this.mobileStatusMessage = 'Bekrefter betaling...';

      // If paying on mobile and returning back to the checkout
      if (this.queryParamsService.getQueryParams().poppedVipps) {
        this.mobileStatusMessage = 'Fullfør betaling...';
        this.returnedFromVipps = true;
        this.manualDeeplinkEnabled = true;
      }
    } else if (this.form.valid) {
      this.loading = false;
      this.initPayment();
    } else {
      this.formLayout = true;
      this.loading = false;
    }
  }

  async initPayment() {
    this.updateErrorMessages();

    if (this.form.invalid) {
      return;
    }

    this.phoneNumber = formatPhoneNumber(
      this.form.value.phone.trim(),
      this.state.market
    );
    this.paymentStatusOtherDevice = 'Forbereder betaling...';

    this.paymentInProgress = true;
    let resp: VippsResponse;

    try {
      if (!this.isPhone) {
        this.error = null;
      }

      let vippsRequestParams: VippsRequestParams = {
        fallbackUrl: window.location.href,
        isMobile: this.shouldOverrideIsPhone ? false : this.isPhone,
        phoneNumber: this.phoneNumber,
        resumeTokenId: this.resumeToken,
      };

      try {
        resp = await this.vippsService.initPayment(vippsRequestParams);
      } catch (err) {
        this.loggingService.warn(
          'vipps frontend: calling vipps api failed',
          err
        );
        this.handleError(err);
      }

      if (!this.handleVippsErrors(resp)) {
        // Start polling for vipps payment completion
        this.vippsLandingRedirectUrl = resp.redirectUrl;
        this.activeDepositId = resp.id;

        // When completing a vipps deposit om mobile, we can not garantee
        // that we will be redirected back to the same broweser tab or browser
        // as the one a payment request was started from -
        // because vipps adds their own query params to the fallback URL we give them!!
        // ...
        // We may, therefore, have multiple browsers or tabs open attempting to get a payment's status
        // By adding there query parameters, we will be able to still poll for the the payment's status.
        // If a vipps deposit was successful, then we will be redirected to the success page
        // in both browsers or tabs without issue.
        if (this.isPhone) {
          this.queryParamsService.setQueryParam('vippsId', resp.id);
          this.queryParamsService.setClientSideParam(this.resumeToken);
        }

        this.startPolling();

        // Open vipps landing page and or app
        if (!resp.checkoutSuccess) {
          if (this.isPhone) {
            this.stopPolling();
            this.loading = false;
            this.mobileStatusMessage =
              'Trykk på knappen under for å åpne Vipps';
            this.openVipps();
          } else {
            this.paymentStatusOtherDevice = 'Åpne Vipps-appen på mobilen';
            this.stopPolling();
            this.openVipps();
          }
        }
      }
    } catch (err) {
      this.loggingService.warn(
        'vipps frontend: got error trying to open vipps',
        err
      );
      this.paymentInProgress = false;
      this.handleFatalError(err);
    }
  }

  async openVipps() {
    return this.vippsService.openVippsUrl(this.vippsLandingRedirectUrl);
  }

  setupForm(): void {
    this.form = this.formBuilder.group({
      phone: [
        String(this.phoneNumber) || '',
        [Validators.required, phoneNumberValidator([47], this.state.market)],
      ],
    });
  }

  updateErrorMessages() {
    this.showError = !this.showError;
  }

  async ngOnDestroy() {
    await this.cancelPayment(this.activeDepositId);
    this.stopPolling();
    this.loggingService.warn(
      'vipps frontend: 99: current url',
      window.location.href
    );
  }

  async forceNavigateHome() {
    await this.cancelPayment(this.activeDepositId);
    this.stopPolling();
    this.vippsService.clearAllQueryParameters();
    this.error = null;
    this.paymentInProgress = false;

    this.router.navigate([NoRoute.HOME], {
      queryParams: {
        // Never preserve these params:
        vippsId: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  async cancelPayment(vippsId: string) {
    // vipps payments can only be cancelled in a specific state
    if (
      this.vippsPaymentStatus === VippsStatus.CREATED ||
      this.vippsPaymentStatus === VippsStatus.AUTHORIZED
    ) {
      try {
        return await this.vippsService.cancelCurrentVippsPayment(vippsId);
      } catch (err) {
        this.loggingService.warn(
          'vipps frontend: cancel payment failed with error',
          err
        );
      }
    }
  }

  restartOnOtherDevice(): void {
    this.vippsService.cancelCurrentVippsPayment(this.activeDepositId);
    this.error = null;
    this.paymentStatusOtherDevice = null;
    this.paymentInProgress = false;
    this.setupForm();
    this.formLayout = true;

    if (this.isPhone) {
      this.shouldOverrideIsPhone = true;
    }
  }

  async restartPaymentMobile() {
    this.paymentStatusOtherDevice = null;
    this.error = null;
    this.formLayout = false;
    this.mobileStatusMessage = 'Forbereder betaling...';
    this.paymentInProgress = true;
  }

  startPolling() {
    this.pollingSubscription = timer(2_000, 2_000)
      .pipe(
        startWith(0),
        switchMap(() => this.vippsService.getStatus())
      )
      .subscribe(
        (res) => this.handleUpdate(res),
        (err) => this.handleLateError(err)
      );
  }

  submitForm(): void {
    if (this.form.valid) {
      this.formLayout = false;
      this.initPayment();
    } else {
      this.showError = true;
    }
  }

  public isPolling(): boolean {
    return (
      this.pollingSubscription !== undefined && !this.pollingSubscription.closed
    );
  }

  public stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.activeDepositId = null;
    this.manualDeeplinkEnabled = true;
  }

  async handleUpdate(res: VippsResponse) {
    this.vippsPaymentStatus = res.status;

    // A payment can give response as captured multiple times if open in multiple tabs.
    // On mobile, this is the default for safari on redirected back from the app
    if (res.checkoutSuccess && this.firstTimeCaptured) {
      this.firstTimeCaptured = !this.firstTimeCaptured;
      this.stopPolling();
      try {
        await this.fetchState();
        await this.navigateFromState();
      } catch (err) {
        this.handleError(err);
      }
    } else if (res.status === VippsStatus.ERROR) {
      this.loggingService.warn(
        'vipps frontend: something went wrong from the backend when handling the polling and vipps deposit'
      );
      this.vippsService.clearAllQueryParameters();
      this.stopPolling();
      this.handleFatalError(res);
    } else if (
      res.status === VippsStatus.TERMINATED ||
      res.status === VippsStatus.ABORTED
    ) {
      this.vippsService.clearAllQueryParameters();
      this.stopPolling();
      this.handleVippsCancelled(res);
    }
  }

  async restartFormBasedPayment() {
    this.paymentStatusOtherDevice = null;
    this.error = null;
    this.formLayout = true;

    try {
      await this.initPayment();
    } catch (err) {
      this.handleFatalError(err);
    }
  }

  getVippsErrorText(error: IVippsError): string {
    return getErrorMessage(error);
  }

  getErrorMessages(): string {
    return this.getVippsErrorText(this.error[0]);
  }

  handleFatalError(err?: any): void {
    this.modalActive = true;
    this.stopPolling();

    const errorMessage = 'Vennligst prøv igjen eller kontakt kundestøtte.';
    if (!!err) {
      this.loggingService.warn('vipps frontend: 94: error', err);
      console.error('fatal vipps error', err);
    }

    const dialogConfiguration = {
      maxHeight: '50vh',
      maxWidth: '80vw',
      data: {
        heading: 'Noe gikk galt',
        buttons: [
          {
            title: errorMessage,
            callback: () => {
              this.forceNavigateHome();
            },
          },
        ],
      },
    };
    this.dialogService.open(ErrorDialogComponent, dialogConfiguration);
  }

  handleLateError(errorResponse?: HttpErrorResponse): void {
    this.modalActive = true;
    this.stopPolling();

    let errors: IVippsError[];
    if (!!errorResponse) {
      errors = errorResponse.error.errors;
    }

    let message = '';
    if (this.deviceDetectorService.getDeviceInfo().os === OS.IOS) {
      message +=
        'Betalingen mislyktes. Prøv igjen eller be om en ny betalingslenke';
    }

    if (!!errors && errors.some((err) => err)) {
      this.loggingService.warn('vipps frontend: late error ', errors[0]);

      let error = getErrorMessage(errors[0]);
      this.queryParamsService.setQueryParam('error', error);
    } else {
      const dialogConfiguration = {
        maxHeight: '50vh',
        maxWidth: '80vw',
        data: {
          heading: 'Betalingen kunne ikke fullføres',
          message: message,
          buttons: [
            {
              title: `Prøv igjen`,
              callback: () => {
                this.forceNavigateHome();
              },
            },
          ],
        },
      };
      this.dialogService.open(ErrorDialogComponent, dialogConfiguration);
    }
  }

  handleVippsCancelled(resp: VippsResponse) {
    this.modalActive = true;
    this.paymentInProgress = false;
    this.stopPolling();

    const dialogConfiguration = {
      maxHeight: '50vh',
      maxWidth: '80vw',
      data: {
        heading: 'Avbrutt betaling',
        message:
          'Betalingen mislyktes. Prøv igjen eller be om en ny betalingslenke',
        buttons: [
          {
            title: `Prøv igjen`,
            callback: () => {
              this.forceNavigateHome();
            },
          },
        ],
      },
    };
    this.dialogService.open(ErrorDialogComponent, dialogConfiguration);
  }

  handleVippsErrors(resp: VippsResponse): boolean {
    let isErrors = false;
    let errors: IVippsError[] = resp.errors;

    if (
      (errors && errors.some((err) => err)) ||
      resp.status === VippsStatus.ERROR
    ) {
      this.loggingService.warn('vipps frontend: handle vipps errors');
      let errorMessages = errors.map((e) => this.getVippsErrorText(e));

      isErrors = true;
      this.modalActive = true;
      this.paymentInProgress = false;
      this.stopPolling();

      const dialogConfiguration = {
        maxHeight: '50vh',
        maxWidth: '80vw',
        data: {
          heading: 'Betalingen kunne ikke fullføres',
          message: errorMessages[0],
          buttons: [
            {
              title: `Prøv igjen`,
              callback: () => {
                this.forceNavigateHome();
              },
            },
          ],
        },
      };
      this.dialogService.open(ErrorDialogComponent, dialogConfiguration);
    }

    return isErrors;
  }

  // Statemanagement and navigation for vipps and other payment methods that can be redirected to the payment flow and "lose" state
  // All applications that are redirected-from- and sent-back-to the Zaver checkout and using the backend-driven state management would need this
  async fetchState(): Promise<CheckoutState> {
    const path: string = this.route.routeConfig.path;

    const token = this.queryParamsService.getQueryParams().id;
    this.stateService.setToken(token);

    const controlParams: StateControlParameters = {
      currentPageName: this.stateService.getStepFromUrl(path),
      token: this.stateService.getToken(),
    };

    if (controlParams.token) {
      const state = await this.stateService.fetchState(controlParams);
      this.headerService.setMarket(state.market);
      this.headerService.setCancelUrl(state.merchantUrls?.onCancelUrl);
      this.state = state;
      return state;
    }
  }

  // Note that this functionality is broken. Not touching this flow right now because it works and no time for that yet, but here are the steps:
  // 1. Resume the state with the resume token. Note that the token is also provided in the query parameters. The HttpSession token is set from the idau code.
  // 2. The return from the resume call does NOT have the actual token inside. When the state value is set, it's actually set to null. -- We should get rid of that!
  // 3. The token is properly reset in the state from the query parameters. State is re-fetched with that.
  async createResumeToken(): Promise<string> {
    const path: string = this.route.routeConfig.path;

    const controlParams: StateControlParameters = {
      currentPageName: this.stateService.getStepFromUrl(path),
      token: this.stateService.getToken(),
    };

    if (controlParams.token) {
      const state = await this.stateService.createResumeStateToken(
        controlParams,
        ResumeTokenType.VIPPS_RESUME_STATE_TOKEN
      );
      return state;
    }
  }

  async resumeState(resumeToken: string) {
    try {
      const state = await this.stateService.resume(resumeToken);
      this.stateService.setToken(state.checkoutToken);
      this.headerService.setMarket(state.market);
      this.headerService.setCancelUrl(state.merchantUrls?.onCancelUrl);
      this.state = state;
      return state;
    } catch (err) {
      this.loggingService.warn(
        'vipps frontend: encounteded an error when resuming state',
        err
      );
      this.handleFatalError(err);
    }
  }

  async navigateFromState() {
    // Normally we would want to use `this.navigate()` to proceed to SUCCESS
    // Reason that we do this is that Vipps uses polling to check if we have succeeded in fulfilling all requirements for a payment
    // The checks if a vipps payment's status is in the correct state, then settles the payment request and assigns a user
    if (this.state && this.state.forceNavigation) {
      const route: string = this.stateService.getRouteFromState(this.state);
      this.router.navigate([route], { queryParamsHandling: 'preserve' });
    }
  }
}
